import React, { useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { BasePageQuotation } from 'design-system';

import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { Quotation, EmissionLog } from 'data-layer-cr/dist/models';
import { paymentCodeForStatusId } from 'data-layer-cr/dist/functions/paymentCodes';

// Redux
import { RootState } from '../../../store/rootReducer';
import { reconnectToEmittingQuotation } from '../../../store/emissionPolicy/actions';

// Functions
import {
  attemptToExtractEmissionStatusCode,
  shouldShowSuccessScreen,
} from '../../../functions/quotationSemaphoreMethods';

// Network
import { CREATE_PRICING } from '../../../store/emissionPolicy/requestIdentifiers';

// Components
import Confirmation from '../EmissionPolicy/Confirmation';
import { ErrorLayout, NetworkProgressDialogue } from '../components';

/** Extracted to prevent sonarcloud from complaining */
const confirmationScreen = (
  <div style={{ marginTop: 90 }}>
    <Confirmation data-testid="confirmation" />
  </div>
);

/** Extracted to prevent sonarcloud from complaining */
export const errorScreen = (paymentType: string): React.ReactNode => (
  <ErrorLayout data-testid="error" paymentType={paymentType} />
);

/**
 * Returns a network dialogue, configured based on the open parameter
 *
 * @param open - Whether the dialogue is open
 */
function makeNetworkProgressDialogue(open: boolean): React.ReactNode {
  return (
    <NetworkProgressDialogue
      data-testid="network-dialogue"
      open={open}
      mainText="Espera un momento"
      caption="Espera un momento, estamos emitiendo tu póliza"
      comment="(no cierres esta ventana)"
    />
  );
}

export const StandaloneFinalEmissionScreen = ({
  quotation,
  emissionLog,
  paymentType,
  reconnectToEmittingQuotationAction,
}: PropsFromRedux): JSX.Element => {
  const creatingStatus = useNetworkObserver(CREATE_PRICING);
  const statusCode = useMemo(
    () =>
      paymentCodeForStatusId(attemptToExtractEmissionStatusCode(emissionLog)),
    [emissionLog],
  );
  const forceSuccess = useMemo(
    () => shouldShowSuccessScreen(quotation, statusCode),
    [quotation, statusCode],
  );

  useEffect(() => {
    if (!forceSuccess) {
      reconnectToEmittingQuotationAction();
    }
  }, [reconnectToEmittingQuotationAction, forceSuccess]);

  const content = useMemo(() => {
    if (forceSuccess) {
      return confirmationScreen;
    }
    switch (creatingStatus) {
      case 'success':
        return confirmationScreen;
      case 'failure':
        return errorScreen(paymentType);
      default:
        return <></>;
    }
  }, [creatingStatus, forceSuccess, paymentType]);

  const networkDialogue = useMemo(
    () => makeNetworkProgressDialogue(creatingStatus === 'requesting'),
    [creatingStatus],
  );

  return (
    <BasePageQuotation rightActions="" leftActions="" backgroundColor="white">
      {content}
      {networkDialogue}
    </BasePageQuotation>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  quotation: Quotation;
  emissionLog: EmissionLog | null;
  paymentType: string;
} => ({
  quotation: state.quotation.quotation,
  emissionLog: state.quotation.emissionLog,
  paymentType: state.emissionPolicy.paymentFields.type,
});

const reduxActions = {
  reconnectToEmittingQuotationAction: reconnectToEmittingQuotation,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(StandaloneFinalEmissionScreen);
